import { Injectable, inject } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { EmailValidatorService } from './public-email.service';

@Injectable({ providedIn: 'root' })
export class DomainValidators {
  private emailValidator = inject(EmailValidatorService);

  /**
   * Validates that the entered domain matches the signed-in user's email domain (or is a
   * subdomain of it) and that neither side is a public email provider.
   *
   * The user's email comes from a GETTER, not a value: the portals load the current user
   * asynchronously (`UserService.currentUser$` / `AuthService.currentUser$`), so the address is
   * frequently still empty when the form — and with it this validator — is built. Capturing it
   * by value there would pin the empty string and make the check silently pass forever. The
   * getter is read per validation run, which is also when the answer actually matters.
   *
   * A getter rather than a service lookup because the two portals resolve the current user
   * differently; this keeps the library free of app-specific dependencies and testable without
   * a session.
   */
  matchesEmailDomain(resolveUserEmail: () => string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const domain = control.value;

      if (!domain || typeof domain !== 'string' || domain.trim() === '') {
        return of(null);
      }

      // Debounce to prevent too many validation passes during rapid typing
      return of(domain).pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map((value) => value.trim()),
        map((domainValue) => {
          // Check if it's a public email domain
          if (this.emailValidator.isPublicEmailDomain(domainValue)) {
            return { publicEmailDomain: true };
          }

          const userEmailDomain = this.emailValidator.extractDomainFromEmail(resolveUserEmail());
          if (!userEmailDomain) {
            return null;
          }

          // Check if the user's own email is from a public provider
          if (this.emailValidator.isPublicEmailDomain(userEmailDomain)) {
            return { userEmailPublic: true };
          }

          return this.isDomainValidForEmail(domainValue, userEmailDomain)
            ? null
            : { domainMismatch: true };
        }),
        catchError(() => of(null))
      );
    };
  }

  /**
   * Checks if a domain is valid for the user's email domain.
   * Valid domains include:
   * 1. Exactly the same domain
   * 2. A subdomain of the email domain
   */
  private isDomainValidForEmail(domain: string, emailDomain: string): boolean {
    const candidate = domain.trim().toLowerCase();
    const reference = emailDomain.toLowerCase();

    return candidate === reference || candidate.endsWith('.' + reference);
  }
}
